import { useEffect, useState } from "react";
import { api, formatCents, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Button, Pill, Loader, EmptyState, Modal, Input, Select, Textarea, Stat } from "@/components/ui/Primitives";
import { Shield, Plus, AlertCircle, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

export default function Insurance() {
  const { user } = useAuth();
  const [policies, setPolicies] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    lease_id: "", carrier: "", policy_number: "",
    liability_coverage: "", contents_coverage: "",
    effective_date: "", expiration_date: "", notes: ""
  });

  const isTenant = user?.role === "tenant";

  const load = async () => {
    setLoading(true);
    try {
      const calls = [
        api.get("/insurance"),
        api.get("/leases"),
      ];
      if (!isTenant) calls.push(api.get("/insurance/compliance").catch(() => ({ data: null })));
      const res = await Promise.all(calls);
      setPolicies(Array.isArray(res[0].data) ? res[0].data : (res[0].data?.policies || []));
      setLeases(Array.isArray(res[1].data) ? res[1].data : (res[1].data?.leases || []));
      if (!isTenant && res[2]) setCompliance(res[2].data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/insurance", {
        lease_id: form.lease_id,
        carrier: form.carrier.trim(),
        policy_number: form.policy_number.trim(),
        liability_coverage_cents: Math.round(Number(form.liability_coverage) * 100),
        contents_coverage_cents: form.contents_coverage ? Math.round(Number(form.contents_coverage) * 100) : 0,
        effective_date: form.effective_date,
        expiration_date: form.expiration_date,
        notes: form.notes || null,
      });
      toast.success("Insurance policy added");
      setOpen(false);
      setForm({ lease_id: "", carrier: "", policy_number: "", liability_coverage: "", contents_coverage: "", effective_date: "", expiration_date: "", notes: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add policy");
    } finally { setSaving(false); }
  };

  if (loading) return <Loader/>;

  return (
    <div>
      <PageHeader
        title="Insurance"
        subtitle={isTenant ? "Add your tenant insurance policy to stay compliant." : "Track tenant insurance compliance across your portfolio."}
        actions={<Button onClick={() => setOpen(true)} disabled={leases.length === 0} data-testid="add-insurance-button"><Plus className="w-4 h-4"/> Add policy</Button>}
      />

      {!isTenant && compliance && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Stat label="Compliant leases" value={compliance.compliant_count || 0} icon={CheckCircle2} accent="text-green-800"/>
          <Stat label="Expiring soon" value={compliance.expiring_count || 0} icon={Clock} accent="text-amber-600"/>
          <Stat label="Non-compliant" value={compliance.non_compliant_count || 0} icon={AlertCircle} accent="text-red-700"/>
          <Stat label="Total active leases" value={compliance.total_leases || leases.length}/>
        </div>
      )}

      {policies.length === 0 ? (
        <EmptyState
          icon={Shield}
          title="No policies yet"
          description={leases.length === 0 ? "Insurance requires an active lease. Create a lease first." : "Add a tenant insurance certificate to track coverage."}
        />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {policies.map((p) => {
            const expDate = p.expiration_date ? new Date(p.expiration_date) : null;
            const daysLeft = expDate ? Math.floor((expDate - new Date()) / 86400000) : null;
            const tone = daysLeft == null ? "slate" : daysLeft < 0 ? "red" : daysLeft < 30 ? "amber" : "green";
            return (
              <Card key={p.policy_id} className="p-5" data-testid={`policy-card-${p.policy_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-display text-lg font-bold text-slate-900">{p.carrier}</div>
                    <div className="text-xs font-mono text-slate-500 mt-0.5">{p.policy_number}</div>
                  </div>
                  <Pill tone={tone}>
                    {daysLeft == null ? "—" : daysLeft < 0 ? "Expired" : daysLeft < 30 ? `${daysLeft}d left` : "Active"}
                  </Pill>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider">Liability</div>
                    <div className="font-semibold text-slate-900">{formatCents(p.liability_coverage_cents)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider">Contents</div>
                    <div className="font-semibold text-slate-900">{formatCents(p.contents_coverage_cents || 0)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider">Effective</div>
                    <div className="font-semibold text-slate-900">{formatDate(p.effective_date)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider">Expires</div>
                    <div className="font-semibold text-slate-900">{formatDate(p.expiration_date)}</div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Add insurance policy" testid="insurance-modal">
        <form onSubmit={submit} className="space-y-4">
          <Select label="Lease" required value={form.lease_id} onChange={(e)=>setForm({...form, lease_id:e.target.value})}>
            <option value="">Select lease</option>
            {leases.map((l) => <option key={l.lease_id} value={l.lease_id}>{`${l.tenant_email} · ${formatCents(l.monthly_rent)}/mo`}</option>)}
          </Select>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Carrier" required value={form.carrier} onChange={(e)=>setForm({...form, carrier:e.target.value})} testid="ins-carrier-input"/>
            <Input label="Policy #" required value={form.policy_number} onChange={(e)=>setForm({...form, policy_number:e.target.value})} testid="ins-policy-input"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Liability (CAD)" type="number" required value={form.liability_coverage} onChange={(e)=>setForm({...form, liability_coverage:e.target.value})}/>
            <Input label="Contents (CAD)" type="number" value={form.contents_coverage} onChange={(e)=>setForm({...form, contents_coverage:e.target.value})}/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Effective date" type="date" required value={form.effective_date} onChange={(e)=>setForm({...form, effective_date:e.target.value})}/>
            <Input label="Expiration date" type="date" required value={form.expiration_date} onChange={(e)=>setForm({...form, expiration_date:e.target.value})}/>
          </div>
          <Textarea label="Notes" value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})}/>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="ins-create-submit">{saving ? "Adding…" : "Add policy"}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
